(() => {
  if (window.__hugcoffeeGiftWrappingLoaded) return;
  window.__hugcoffeeGiftWrappingLoaded = true;

  let operationQueue = Promise.resolve();
  let configuration = null;
  let miniViewHTML = null;
  let checkoutPending = false;
  let themeRequests = 0;
  const rules = window.hugcoffeeCartGiftRules;

  document.addEventListener('theme:loading:start', () => { themeRequests += 1; });
  document.addEventListener('theme:loading:end', () => { themeRequests = Math.max(0, themeRequests - 1); });

  const waitForTheme = () => new Promise((resolve, reject) => {
    if (themeRequests === 0) return resolve();
    const timeout = setTimeout(() => {
      document.removeEventListener('theme:loading:end', onEnd);
      reject(new Error('カートの更新が完了していません。少し待ってからもう一度お試しください。'));
    }, 15000);
    const onEnd = () => {
      if (themeRequests !== 0) return;
      clearTimeout(timeout);
      document.removeEventListener('theme:loading:end', onEnd);
      resolve();
    };
    document.addEventListener('theme:loading:end', onEnd);
  });

  const cartRoot = () => window.Shopify?.routes?.root || "/";

  const enqueue = (operation) => {
    const run = async () => { await waitForTheme(); return operation(); };
    operationQueue = operationQueue.then(run, run);
    return operationQueue;
  };

  const collectSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections },
      })
    );
    return [...new Set(sections)];
  };

  const requestCart = async (endpoint, body) => {
    const response = await fetch(`${cartRoot()}cart/${endpoint}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || "カートを更新できませんでした。");
    return result;
  };

  const lineQuantities = (cart) => rules.lineQuantities(cart, configuration);
  const correctionsFor = (cart, preferredType = null) => rules.correctionsFor(cart, configuration, preferredType);

  const normalizeCart = async (cart, preferredType = null) => {
    const updates = correctionsFor(cart, preferredType);
    if (Object.keys(updates).length === 0) return cart;

    return requestCart("update", {
      updates,
      sections: collectSections().join(","),
    });
  };

  const updateFreeShippingBars = (cart) => {
    const normalShippingTotal = cart.items
      .filter((item) => item.requires_shipping && rules.roleOf(item, configuration) === "normal")
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscount = cart.cart_level_discount_applications
      .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);

    document.querySelectorAll("free-shipping-bar").forEach((bar) => {
      bar.setAttribute("total-price", Math.max(0, normalShippingTotal - cartDiscount));
    });
  };

  const publishCart = (cart) => {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "gift-wrapping:change", cart },
      })
    );
    // Run after the theme's synchronous cart listeners, which also update this bar.
    updateFreeShippingBars(cart);
  };

  const completeUpdate = async (cart, preferredType = null) => {
    const normalizedCart = await normalizeCart(await fetchCart(), preferredType);

    if (window.themeVariables?.settings?.pageType === "cart") {
      if (!checkoutPending) window.location.reload();
      return normalizedCart;
    }

    publishCart(await fetchCartWithSections());
    return normalizedCart;
  };

  const readConfiguration = (root) => {
    const node = root.querySelector('[data-cart-gift-state]');
    if (!node) throw new Error('カートを確認できません。ページを再読み込みしてください。');
    const state = JSON.parse(node.textContent);
    if (!Array.isArray(state.cart?.items) || !Array.isArray(state.miniProductIds) || !Array.isArray(state.candidates)) {
      throw new Error('カート情報を確認できません。ページを再読み込みしてください。');
    }
    configuration = state;
    return state.cart;
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartRoot()}?section_id=cart-gift-state`, { cache: 'no-store' });
    if (!response.ok) throw new Error('カートを更新できませんでした。もう一度お試しください。');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cart = readConfiguration(doc);
    miniViewHTML = doc.querySelector('[data-mini-gift-view]')?.innerHTML ?? null;
    return cart;
  };

  const refreshMiniView = () => {
    const current = document.querySelector('[data-mini-gift]');
    if (!current || miniViewHTML === null) return;
    const fragment = document.createElement('template');
    fragment.innerHTML = miniViewHTML;
    const next = fragment.content.querySelector('[data-mini-gift]');
    if (next) current.replaceWith(next);
  };

  const resyncAfterError = async () => {
    try {
      const cart = await fetchCart();
      refreshMiniView();
      updateFreeShippingBars(cart);
    } catch (error) {
      console.warn('Cart resynchronization failed', error);
    }
  };

  const fetchCartWithSections = async () => requestCart("update", {
    updates: {},
    sections: collectSections().join(","),
  });

  const setBusy = (busy) => {
    document.querySelectorAll("gift-wrapping, [data-mini-gift]").forEach((element) => {
      if (busy || checkoutPending) element.setAttribute("aria-busy", "true");
      else element.removeAttribute("aria-busy");
    });
  };

  const showError = (message) => {
    if (message && !document.querySelector('[data-cart-gift-error]')) {
      const form = document.querySelector('form.cart-page[data-shipping-preference-cart]') || document.querySelector('form[data-shipping-preference-cart]');
      if (form) {
        const error = document.createElement('p');
        error.setAttribute('data-cart-gift-error', '');
        error.setAttribute('role', 'alert');
        form.prepend(error);
      }
    }
    document.querySelectorAll(".gift-wrapping__error, .mini-gift__error, [data-cart-gift-error]").forEach((element) => {
      element.textContent = message;
      element.hidden = !message;
    });
  };

  const updateGift = (action) => {
    if (checkoutPending) return;
    return enqueue(async () => {
    setBusy(true);
    showError("");

    try {
      let cart = await normalizeCart(await fetchCart());
      const state = lineQuantities(cart);
      let preferredType = null;

      if (state.normal === 0) return await completeUpdate(cart);

      if (action === "wrap") {
        if (state.wrap > 0) {
          cart = await requestCart("update", {
            updates: Object.fromEntries(state.wrapLines.map((item) => [item.key, 0])),
            sections: collectSections().join(","),
          });
        } else {
          const removals = Object.fromEntries(state.materialLines.map((item) => [item.key, 0]));
          if (Object.keys(removals).length) cart = await requestCart("update", { updates: removals });
          await requestCart("add", {
            items: [{ id: configuration.wrapVariantId, quantity: 1 }],
          });
          cart = await fetchCartWithSections();
          preferredType = "wrap";
        }
      } else if (action === "materials") {
        const removals = Object.fromEntries(state.wrapLines.map((item) => [item.key, 0]));
        if (Object.keys(removals).length) cart = await requestCart("update", { updates: removals });
        await requestCart("add", {
          items: [{ id: configuration.materialsVariantId, quantity: 1 }],
        });
        cart = await fetchCartWithSections();
        preferredType = "materials";
      } else if (action === "materials-increase" && state.materials < state.normal) {
        cart = await requestCart("change", {
          id: state.materialLines[0].key,
          quantity: state.materials + 1,
          sections: collectSections().join(","),
        });
        preferredType = "materials";
      } else if (action === "materials-decrease" && state.materials > 0) {
        cart = await requestCart("change", {
          id: state.materialLines[0].key,
          quantity: state.materials - 1,
          sections: collectSections().join(","),
        });
        preferredType = "materials";
      }

      await completeUpdate(cart, preferredType);
    } catch (error) {
      await resyncAfterError();
      showError(error.message);
    } finally {
      setBusy(false);
    }
    }).catch(error => showError(error.message));
  };

  class GiftWrapping extends HTMLElement {
    connectedCallback() {
      this.addEventListener("click", (event) => {
        const button = event.target.closest("[data-gift-action]");
        if (!button || button.disabled) return;
        updateGift(button.dataset.giftAction);
      });
    }
  }

  if (!customElements.get("gift-wrapping")) customElements.define("gift-wrapping", GiftWrapping);

  const updateMiniGift = (action, productId) => {
    if (checkoutPending) return;
    return enqueue(async () => {
    setBusy(true);
    showError('');
    try {
      const cart = await normalizeCart(await fetchCart());
      const state = lineQuantities(cart);
      if (state.normal === 0) return await completeUpdate(cart);
      const lines = state.miniLines.filter(item => item.product_id === productId);
      const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
      const candidate = configuration.candidates.find(item => item.productId === productId);
      if (action === 'none' && state.miniLines.length) {
        await requestCart('update', { updates: Object.fromEntries(state.miniLines.map(item => [item.key, 0])) });
      } else if (action === 'remove' && lines.length) {
        await requestCart('update', { updates: Object.fromEntries(lines.map(item => [item.key, 0])) });
      } else if (action === 'decrease' && lines.length) {
        const last = lines[lines.length - 1];
        await requestCart('change', { id: last.key, quantity: last.quantity - 1 });
      } else if (action === 'add' && configuration.miniEnabled && rules.usableCandidate(candidate)
        && state.mini < configuration.maxTotal && quantity < configuration.maxPerGift && quantity < candidate.inventory) {
        await requestCart('add', { items: [{ id: candidate.variantId, quantity: 1 }] });
      }
      await completeUpdate(cart);
    } catch (error) {
      await resyncAfterError();
      showError(error.message);
    } finally {
      setBusy(false);
    }
    }).catch(error => showError(error.message));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mini-action]');
    if (!button || button.disabled || !button.closest('[data-mini-gift]')) return;
    updateMiniGift(button.dataset.miniAction, Number(button.dataset.productId));
  });

  document.addEventListener('cart:change', (event) => {
    if (!configuration || checkoutPending || event.detail?.baseEvent === 'gift-wrapping:change') return;
    enqueue(async () => {
      const cart = await fetchCart();
      const normalized = await normalizeCart(cart);
      if (normalized !== cart) {
        await fetchCart();
        publishCart(normalized);
      }
      else updateFreeShippingBars(normalized);
      refreshMiniView();
    }).catch(error => showError(error.message));
  });

  const initialize = () => {
    if (!document.querySelector('[data-cart-gift-state]')) return;
    const initial = readConfiguration(document);
    if (!initial.items.length) return;
    enqueue(async () => {
      const cart = await fetchCart();
      const normalized = await normalizeCart(cart);
      updateFreeShippingBars(normalized);
      if (normalized !== cart) {
        if (window.themeVariables?.settings?.pageType === 'cart') {
          if (!checkoutPending) window.location.reload();
        }
        else publishCart(normalized);
      }
    }).catch(error => showError(error.message));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  // The SHIPPING preference handler is the sole checkout submit interceptor.
  window.hugcoffeeCartGifts = {
    prepareCheckout: () => {
      checkoutPending = true;
      setBusy(true);
      return enqueue(async () => {
        showError('');
        const cart = await normalizeCart(await fetchCart());
        updateFreeShippingBars(cart);
        if (lineQuantities(cart).normal === 0) throw new Error('通常商品をカートに追加してからお進みください。');
        return cart;
      });
    },
    releaseCheckout: () => { checkoutPending = false; setBusy(false); },
    showError,
  };
})();
